import { useMutation } from "@tanstack/react-query"
import { chunk } from "es-toolkit"

import { useAxiosClientContext } from "@/components/core/AxiosInstanceProvider/useAxiosClient"
import { parseDataFromAPI } from "@/hooks/query-api"

import {
  type CheckedRow,
  checkResponseSchema,
  type ValidParsedRow,
  writeResponseSchema,
} from "./schemas"

const CHECK_CHUNK_SIZE = 500

type CheckRowsInput = {
  onProgress?: (completed: number, total: number) => void
  rows: ValidParsedRow[]
}

function useMutationCheckRows() {
  const axiosClient = useAxiosClientContext()

  return useMutation({
    mutationFn: async ({ onProgress, rows }: CheckRowsInput) => {
      const allChecked: CheckedRow[] = []
      const totalRows = rows.length
      const chunks = chunk(rows, CHECK_CHUNK_SIZE)
      onProgress?.(allChecked.length, totalRows)

      for (const rowChunk of chunks) {
        const rows = rowChunk.map((r) => ({
          email: r.email,
          noCall: r.noCall,
          phone: r.phone,
          rowIndex: r.rowIndex,
        }))

        const originalByIndex = new Map(rows.map((row) => [row.rowIndex, row]))

        const { data } = await axiosClient.post("/students/no-call-csv/check", {
          rows,
        })
        const parsed = await parseDataFromAPI(data, checkResponseSchema)

        const checkedRows = parsed.data.rows.map((serverRow) => {
          const original = originalByIndex.get(serverRow.rowIndex)

          return {
            ...serverRow,
            email: original?.email,
            noCall: original?.noCall ?? true,
            parseStatus: "valid" as const,
            phone: original?.phone,
            writeStatus: "pending" as const,
          }
        })

        allChecked.push(...checkedRows)
        onProgress?.(allChecked.length, totalRows)
      }

      return allChecked
    },
  })
}

type WriteRowInput = {
  email?: string
  noCall: boolean
  phone?: string
}

type WriteRowResult = Awaited<ReturnType<typeof parseWriteResponse>>

type WriteRowOptions = {
  onError?: (error: Error) => void
  onSuccess?: (data: WriteRowResult) => void
}

async function parseWriteResponse(data: unknown) {
  return parseDataFromAPI(data, writeResponseSchema)
}

function useMutationWriteRow(options?: WriteRowOptions) {
  const axiosClient = useAxiosClientContext()

  return useMutation<WriteRowResult, Error, WriteRowInput>({
    mutationFn: async (row) => {
      const { data } = await axiosClient.post("/students/no-call-csv/write", {
        email: row.email,
        noCall: row.noCall,
        phone: row.phone,
      })

      return parseWriteResponse(data)
    },
    onError: options?.onError,
    onSuccess: options?.onSuccess,
  })
}

export { useMutationCheckRows, useMutationWriteRow }
